/**
 * Single source of truth for which backend API version the app targets.
 *
 * New/unpinned endpoints default to CURRENT_API_VERSION. Bump this constant when the
 * backend ships a version that's safe to use as the new default going forward.
 *
 * versionedUrl() resolves a version in this order:
 *   1. An explicit version passed at the call site, e.g. versionedUrl('Worksheet/GetByID', 'V3')
 *   2. A controller-level override below, e.g. every QA/* endpoint running on V3
 *   3. CURRENT_API_VERSION
 */
export const CURRENT_API_VERSION = 'V4';

/**
 * Pins every endpoint under a controller to a specific version, without touching each
 * call site. Example: CONTROLLER_VERSION_OVERRIDES.QA = 'V3' would run every QA/* endpoint
 * on V3 until the backend catches that whole controller up to CURRENT_API_VERSION.
 */
const CONTROLLER_VERSION_OVERRIDES: Record<string, string> = {
  // AccountSettings: 'V4',
  // Accountancy: 'V4',
  // Address: 'V4',
  // BackgroundCheck: 'V4',
  // Catalog: 'V4',
  // Configuration: 'V4',
  // Customer: 'V4',
  // Dashboard: 'V4',
  // Debtor: 'V4',
  // Expense: 'V4',
  // File: 'V4',
  // Fitter: 'V4',
  // FitterHour: 'V4',
  // Inventory: 'V4',
  // Login: 'V4',
  // Message: 'V4',
  // Offer: 'V4',
  // Partner: 'V4',
  // Planning: 'V4',
  // Project: 'V4',
  // PurchaseInvoice: 'V4',
  // QA: 'V4',
  // user: 'V4',
  // Weather: 'V4',
  // Worksheet: 'V4'
};

/**
 * Builds a versioned API path, e.g. versionedUrl('Worksheet/GetByID') -> 'V4/Worksheet/GetByID'.
 * Pass version to pin this specific call site to something other than the resolved default.
 */
export function versionedUrl(path: string, version?: string): string {
  const controller = path.split('/')[0];
  const resolvedVersion = version ?? CONTROLLER_VERSION_OVERRIDES[controller] ?? CURRENT_API_VERSION;

  return `${resolvedVersion}/${path}`;
}
